import React, { useState, useEffect } from 'react';
import { Plus, FileText } from "lucide-react";
import { AddDesign } from "../../client_contact_stages/verve-design/components/AddDesign";
import { designService } from "../../client_contact_stages/verve-design/services/designService";
import API, { BASE_URL } from "@/lib/API";
import { showSuccessMessage, showApiError } from "@/lib/toast";
import toast from 'react-hot-toast';

import { LeadExecutionTable } from './lead_ececution_table';
import { LeadExecutionDocumentViewer } from './LeadExecutionDocumentViewer';
import { ActionView, ActionDownload, ActionApprove, ActionReject } from "@/components/ui/LatestTableActions";

export const DocumentsTab = ({ project, onUpdate }) => {
  const [designs, setDesigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // View Modal state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  const fetchDesigns = async () => {
    try {
      setIsLoading(true);
      const res = await API.get(`/lead-executions-designs/${project._id}/designs`);
      setDesigns(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching documents:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (project?._id) {
      fetchDesigns();
    }
  }, [project?._id]);

  const handleUploadSubmit = async (formDataArray) => {
    try {
      if (formDataArray && formDataArray.length > 0) {
        for (const formData of formDataArray) {
          // targetStatus forces 'type' field which backend expects as stage
          await designService.uploadMedia(formData);
        }
        showSuccessMessage("Document(s) uploaded successfully");
      }
      setIsAddModalOpen(false);
      fetchDesigns();
    } catch (err) {
      showApiError(err);
    }
  };

  const handleView = (file) => {
    const url = file.url || `${BASE_URL}/api/design/media/${file.fileId}/${file.filename}`;
    setViewerFile({ url, name: file.filename || file.name });
    setViewerOpen(true);
  };
  
  const handleApprove = async (file) => {
    try {
      await API.patch(`/design/${file.designId}/${file.fileId}/approve`, { status: 'Approved' });
      toast.success(`Approved ${file.filename || file.name}`);
      fetchDesigns();
    } catch (error) {
      showApiError(error);
    }
  };

  const handleReject = async (file) => {
    try {
      await API.patch(`/design/${file.designId}/${file.fileId}/approve`, { status: 'Rejected' });
      toast.success(`Rejected ${file.filename || file.name}`);
      fetchDesigns();
    } catch (error) {
      showApiError(error);
    }
  };

  const handleDownload = async (file) => {
    const url = file.url || `${BASE_URL}/api/design/media/${file.fileId}/${file.filename}`;
    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('blum_token')}`
        }
      });
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.filename || file.name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download file");
    }
  };

  const columns = [
    {
      header: "File Name",
      render: (file) => (
        <div 
          className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors"
          onClick={() => handleView(file)}
        >
          <FileText className="w-4 h-4 text-slate-400" />
          <span className="font-semibold text-[#65768e] text-[11px] truncate max-w-[200px]" title={file.filename || file.name}>
            {file.filename || file.name}
          </span>
        </div>
      )
    },
    {
      header: "Stage",
      render: (file) => (
        <span className="text-[#65768e] text-[11px] font-semibold capitalize">
          {(file.stage || 'other').replace(/_/g, ' ')}
        </span>
      )
    },
    {
      header: "Status",
      render: (file) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
          file.status === 'Approved' ? 'bg-emerald-100 text-emerald-700' :
          file.status === 'Rejected' ? 'bg-red-100 text-red-700' :
          'bg-amber-100 text-amber-700'
        }`}>
          {file.status || 'Pending'}
        </span>
      )
    },
    {
      header: "Size",
      render: (file) => (
        <span className="text-[#65768e] text-[11px] font-semibold">
          {file.size ? (file.size / 1024 / 1024).toFixed(2) : '0'} MB
        </span>
      )
    },
    {
      header: "Uploaded By",
      render: (file) => (
        <div className="flex flex-col">
          <span className="text-[#65768e] text-[11px] font-semibold">{file.uploadedBy?.name || '—'}</span>
          {file.uploadedBy?.role && (
            <span className="text-slate-400 text-[9px] uppercase tracking-wider font-bold mt-0.5">
              {file.uploadedBy.role.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      )
    },
    {
      header: "Date",
      render: (file) => (
        <span className="text-[#65768e] text-[11px] font-semibold">
          {new Date(file.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      )
    },
    {
      header: "Actions",
      render: (file) => (
        <div className="flex items-center gap-1.5">
          <ActionView onClick={() => handleView(file)} title="View" />
          <ActionDownload onClick={() => handleDownload(file)} title="Download" />
          <ActionApprove onClick={() => handleApprove(file)} title="Approve" />
          <ActionReject onClick={() => handleReject(file)} title="Reject" />
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col animate-in fade-in duration-500">
      <div className="flex flex-col w-full">
        <div className="p-1 mb-2 flex items-center justify-end gap-3">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold shadow-sm transition-colors border border-[#2b448f] bg-white text-[#2b448f] hover:bg-slate-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Upload Document
          </button>
        </div>
        
        <LeadExecutionTable 
          columns={columns} 
          data={designs.filter(d => d.stage === 'documents')} 
          isLoading={isLoading} 
        />
      </div>

      <AddDesign
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleUploadSubmit}
        initialData={project.leadId || project.leadDetails}
        targetStatus="documents"
        titleOverride="Upload Document"
      />
      
      <LeadExecutionDocumentViewer 
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewerFile?.url}
        fileName={viewerFile?.name}
      />
    </div>
  );
};

export default DocumentsTab;
